/**
 * primitives.jsx — shared design-system components (PR ⑦).
 *
 * Ported from the Claude Design prototype (`mono-core.jsx`), with one
 * deliberate change: the prototype prop-drills `{ P, TP }` into every
 * component because it runs as browser globals with no context. This app has a
 * real ThemeProvider, so these read `useTheme()` directly — same visuals, no
 * drilling, and they can never render against a stale palette.
 *
 * Scope note: `Btn`, `Stat`, `Mark` and `Ttl` already exist in App.js, already
 * use the design tokens correctly, and are used across many call sites. They
 * are intentionally NOT duplicated or rewritten here — doing so would be
 * cosmetic churn with real regression risk and no functional gain. This module
 * adds only what was missing.
 */

import React, { useState } from 'react';
import { useTheme } from './theme';

/* ── labels ───────────────────────────────────────────────────────────── */

/** Small uppercase kicker above a title. */
export function Eyebrow({ children }) {
  const { P, MO } = useTheme();
  return (
    <div style={{
      fontFamily: MO, fontSize: 10, letterSpacing: 1.4,
      textTransform: 'uppercase', color: P.muted,
    }}>{children}</div>
  );
}

/** Section header for a left-rail card, with an optional right-aligned suffix. */
export function RailHead({ label, suffix }) {
  const { P, MO } = useTheme();
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      paddingBottom: 6, borderBottom: `0.5px solid ${P.ruleSoft}`,
    }}>
      <span style={{
        fontFamily: MO, fontSize: 10.5, letterSpacing: 1.2,
        textTransform: 'uppercase', color: P.muted, fontWeight: 500,
      }}>{label}</span>
      {suffix != null && (
        <span style={{ fontFamily: MO, fontSize: 9.5, color: P.dim, letterSpacing: 0.4 }}>{suffix}</span>
      )}
    </div>
  );
}

/** Status chip. Tones map onto the palette's semantic triples. */
export function Pill({ tone = 'neutral', children }) {
  const { P, MO } = useTheme();
  const c = {
    ok: { fg: P.ok, bg: P.okSoft, line: P.okLine },
    warn: { fg: P.warn, bg: P.warnSoft, line: P.warnLine },
    danger: { fg: P.danger, bg: P.dangerSoft, line: P.dangerLine },
    accent: { fg: P.accent, bg: P.accentSoft, line: P.accentLine },
    neutral: { fg: P.muted, bg: 'transparent', line: P.rule },
  }[tone] || { fg: P.muted, bg: 'transparent', line: P.rule };
  return (
    <span style={{
      fontFamily: MO, fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase',
      padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      color: c.fg, background: c.bg, border: `0.5px solid ${c.line}`,
    }}>{children}</span>
  );
}

/* ── inputs ───────────────────────────────────────────────────────────── */

/** Inline-labelled numeric field (unitless — counts, multipliers, etc.). */
export function NumField({ label, value, onChange, min, max, step = 'any', disabled = false }) {
  const { P, MO } = useTheme();
  return (
    <label style={{
      display: 'flex', alignItems: 'stretch', borderRadius: 6,
      border: `0.5px solid ${P.rule}`, background: P.elev, overflow: 'hidden',
      opacity: disabled ? 0.55 : 1,
    }}>
      <span style={{
        padding: '6px 8px', fontFamily: MO, fontSize: 10.5, color: P.dim,
        background: P.surface, borderRight: `0.5px solid ${P.ruleSoft}`,
        letterSpacing: 0.6, textTransform: 'uppercase', display: 'flex', alignItems: 'center',
      }}>{label}</span>
      <input
        type="number" value={value} min={min} max={max} step={step} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        style={{
          flex: 1, width: 0, minWidth: 0, background: 'transparent', border: 0, outline: 'none',
          fontFamily: MO, fontSize: 12, color: P.ink, padding: '6px 8px', textAlign: 'right',
        }} />
    </label>
  );
}

/**
 * Unit-aware dimension field.
 *
 * Canonical value is ALWAYS millimetres; the field displays and accepts the
 * active display unit and converts on commit. Free typing is held in local
 * state so a partial entry ("1." while typing "1.5") is never round-tripped
 * through the mm conversion mid-keystroke — that would fight the user's cursor.
 * Commit happens on blur or Enter.
 */
export function DimField({ label, mm, onChangeMm, disabled = false }) {
  const { P, MO, unit, toDisplay, fromDisplay } = useTheme();
  const [text, setText] = useState(null);   // null ⇒ not editing; show canonical

  const shown = toDisplay(mm ?? 0);
  const display = text != null
    ? text
    : (unit === 'in' ? Number(shown).toFixed(2) : String(Math.round(Number(shown))));

  const commit = (raw) => {
    const v = parseFloat(raw);
    onChangeMm(Number.isFinite(v) ? fromDisplay(v) : 0);
    setText(null);                          // hand control back to the canonical value
  };

  return (
    <label style={{
      display: 'flex', alignItems: 'stretch', borderRadius: 6,
      border: `0.5px solid ${P.rule}`, background: P.elev, overflow: 'hidden',
      opacity: disabled ? 0.55 : 1,
    }}>
      <span style={{
        padding: '6px 8px', fontFamily: MO, fontSize: 10.5, color: P.dim,
        background: P.surface, borderRight: `0.5px solid ${P.ruleSoft}`,
        letterSpacing: 0.6, textTransform: 'uppercase', display: 'flex', alignItems: 'center',
      }}>{label}</span>
      <input
        type="number" step={unit === 'in' ? 0.05 : 1} value={display} disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        style={{
          flex: 1, width: 0, minWidth: 0, background: 'transparent', border: 0, outline: 'none',
          fontFamily: MO, fontSize: 12, color: P.ink, padding: '6px 4px', textAlign: 'right',
        }} />
      <span style={{
        padding: '6px 8px 6px 2px', fontFamily: MO, fontSize: 10,
        color: P.dim, display: 'flex', alignItems: 'center',
      }}>{unit}</span>
    </label>
  );
}

/** in / mm switch. Wired to the theme's unit by default. */
export function UnitToggle({ size = 'md' }) {
  const { P, MO, unit, setUnit } = useTheme();
  const pad = size === 'sm' ? '2px 7px' : '3px 9px';
  return (
    <div style={{
      display: 'inline-flex', gap: 2, padding: 2, background: P.bg,
      borderRadius: 6, border: `0.5px solid ${P.rule}`,
    }}>
      {['in', 'mm'].map((u) => (
        <button key={u} onClick={() => setUnit(u)} style={{
          padding: pad, borderRadius: 4, cursor: 'pointer', border: 'none',
          fontFamily: MO, fontSize: 10.5, letterSpacing: 0.3,
          background: unit === u ? P.ink : 'transparent',
          color: unit === u ? P.sheet : P.muted,
          fontWeight: unit === u ? 500 : 400,
        }}>{u}</button>
      ))}
    </div>
  );
}
